import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react'
import {
  AccountSettings,
  BankAccount,
  Plan,
  PlanSettings,
  IncomeRule,
  ExpenseRule,
  BudgetItem,
  OneOffTransaction,
  BalanceAdjustment,
  ActualEntry,
  BalanceTarget,
  ShopMonth,
  Transfer,
} from './types'
import { mergeShopSpending } from './lib/spending'
import { isExampleItem } from './lib/storage'

/** Changes to one account's own lists and settings. */
export type AccountAction =
  | { type: 'SET_SETTINGS'; payload: Partial<AccountSettings> }
  | { type: 'ADD_INCOME'; payload: IncomeRule }
  | { type: 'UPDATE_INCOME'; payload: IncomeRule }
  | { type: 'DELETE_INCOME'; payload: string }
  | { type: 'ADD_EXPENSE'; payload: ExpenseRule }
  | { type: 'UPDATE_EXPENSE'; payload: ExpenseRule }
  | { type: 'DELETE_EXPENSE'; payload: string }
  | { type: 'ADD_BUDGET'; payload: BudgetItem }
  | { type: 'UPDATE_BUDGET'; payload: BudgetItem }
  | { type: 'DELETE_BUDGET'; payload: string }
  | { type: 'ADD_ONEOFF'; payload: OneOffTransaction }
  | { type: 'UPDATE_ONEOFF'; payload: OneOffTransaction }
  | { type: 'DELETE_ONEOFF'; payload: string }
  | { type: 'ADD_ADJUSTMENT'; payload: BalanceAdjustment }
  | { type: 'UPDATE_ADJUSTMENT'; payload: BalanceAdjustment }
  | { type: 'DELETE_ADJUSTMENT'; payload: string }
  | { type: 'SET_ACTUAL'; payload: ActualEntry }
  | { type: 'CLEAR_ACTUAL'; payload: { sourceId: string; date: string } }
  | { type: 'ADD_TARGET'; payload: BalanceTarget }
  | { type: 'UPDATE_TARGET'; payload: BalanceTarget }
  | { type: 'DELETE_TARGET'; payload: string }
  /**
   * A bank statement import, applied in one go so it saves as one change.
   * Actuals are whole entries to upsert; budgets replace those with the same id.
   */
  | {
      type: 'APPLY_IMPORT'
      payload: {
        oneOffs: OneOffTransaction[]
        actuals: ActualEntry[]
        budgets: BudgetItem[]
        expenses: ExpenseRule[]
        /** Income whose match patterns the import changed (SM-38). Optional: older callers send none. */
        incomes?: IncomeRule[]
        /** Pay and bills spotted in the statement and ticked to add (SM-30). */
        newIncomes?: IncomeRule[]
        newExpenses?: ExpenseRule[]
        /** Take out the starter plan's untouched examples: the statement's pay and bills replace them (SM-31). */
        replaceExamples?: boolean
        adjustment?: BalanceAdjustment
        /** This import's spending per shop and month, added to what earlier imports recorded. */
        shopSpending?: ShopMonth[]
        /** The statement's account fingerprint, for an account that has none yet (SM-25). */
        statementFingerprint?: string
      }
    }

export type Action =
  | { type: 'ACCOUNT'; accountId: string; action: AccountAction }
  | { type: 'SET_PLAN_SETTINGS'; payload: Partial<PlanSettings> }
  | { type: 'ADD_ACCOUNT'; payload: BankAccount }
  /** Name, settings, interest and whether it counts towards the total — not its lists. */
  | {
      type: 'UPDATE_ACCOUNT'
      payload: { id: string } & Partial<Pick<BankAccount, 'name' | 'includeInTotal' | 'settings' | 'interest'>>
    }
  | { type: 'DELETE_ACCOUNT'; payload: string }
  /** Transfers belong to the plan rather than either account, so they're edited here. */
  | { type: 'ADD_TRANSFER'; payload: Transfer }
  | { type: 'UPDATE_TRANSFER'; payload: Transfer }
  | { type: 'DELETE_TRANSFER'; payload: string }
  | { type: 'REPLACE_STATE'; payload: Plan }

function accountReducer(state: BankAccount, action: AccountAction): BankAccount {
  switch (action.type) {
    case 'SET_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } }
    case 'ADD_INCOME':
      return { ...state, incomes: [...state.incomes, action.payload] }
    case 'UPDATE_INCOME':
      return { ...state, incomes: state.incomes.map((i) => (i.id === action.payload.id ? action.payload : i)) }
    case 'DELETE_INCOME':
      return { ...state, incomes: state.incomes.filter((i) => i.id !== action.payload) }
    case 'ADD_EXPENSE':
      return { ...state, expenses: [...state.expenses, action.payload] }
    case 'UPDATE_EXPENSE':
      return { ...state, expenses: state.expenses.map((i) => (i.id === action.payload.id ? action.payload : i)) }
    case 'DELETE_EXPENSE':
      return { ...state, expenses: state.expenses.filter((i) => i.id !== action.payload) }
    case 'ADD_BUDGET':
      return { ...state, budgets: [...state.budgets, action.payload] }
    case 'UPDATE_BUDGET':
      return { ...state, budgets: state.budgets.map((i) => (i.id === action.payload.id ? action.payload : i)) }
    case 'DELETE_BUDGET':
      return { ...state, budgets: state.budgets.filter((i) => i.id !== action.payload) }
    case 'ADD_ONEOFF':
      return { ...state, oneOffs: [...state.oneOffs, action.payload] }
    case 'UPDATE_ONEOFF':
      return { ...state, oneOffs: state.oneOffs.map((i) => (i.id === action.payload.id ? action.payload : i)) }
    case 'DELETE_ONEOFF':
      return { ...state, oneOffs: state.oneOffs.filter((i) => i.id !== action.payload) }
    case 'ADD_ADJUSTMENT':
      return { ...state, adjustments: [...state.adjustments, action.payload] }
    case 'UPDATE_ADJUSTMENT':
      return { ...state, adjustments: state.adjustments.map((i) => (i.id === action.payload.id ? action.payload : i)) }
    case 'DELETE_ADJUSTMENT':
      return { ...state, adjustments: state.adjustments.filter((i) => i.id !== action.payload) }
    case 'SET_ACTUAL': {
      // One actual per (source, occurrence date) — upsert rather than append.
      const rest = state.actuals.filter(
        (a) => !(a.sourceId === action.payload.sourceId && a.date === action.payload.date),
      )
      return { ...state, actuals: [...rest, action.payload] }
    }
    case 'CLEAR_ACTUAL':
      return {
        ...state,
        actuals: state.actuals.filter(
          (a) => !(a.sourceId === action.payload.sourceId && a.date === action.payload.date),
        ),
      }
    case 'ADD_TARGET':
      return { ...state, targets: [...state.targets, action.payload] }
    case 'UPDATE_TARGET':
      return { ...state, targets: state.targets.map((i) => (i.id === action.payload.id ? action.payload : i)) }
    case 'DELETE_TARGET':
      return { ...state, targets: state.targets.filter((i) => i.id !== action.payload) }
    case 'APPLY_IMPORT': {
      const {
        oneOffs,
        actuals,
        budgets,
        expenses,
        incomes = [],
        newIncomes = [],
        newExpenses = [],
        replaceExamples,
        adjustment,
        shopSpending,
        statementFingerprint,
      } = action.payload
      const keep = <T extends IncomeRule | ExpenseRule | BudgetItem>(item: T) => !replaceExamples || !isExampleItem(item)
      const replaced = new Set(actuals.map((a) => `${a.sourceId}|${a.date}`))
      return {
        ...state,
        ...(shopSpending && shopSpending.length > 0
          ? { shopSpending: mergeShopSpending(state.shopSpending ?? [], shopSpending) }
          : {}),
        ...(statementFingerprint ? { statementFingerprint } : {}),
        oneOffs: [...state.oneOffs, ...oneOffs],
        actuals: [...state.actuals.filter((a) => !replaced.has(`${a.sourceId}|${a.date}`)), ...actuals],
        budgets: state.budgets.filter(keep).map((b) => budgets.find((u) => u.id === b.id) ?? b),
        incomes: [...state.incomes.filter(keep), ...newIncomes].map((i) => incomes.find((u) => u.id === i.id) ?? i),
        // New ones first: the same import may have taught one its merchant.
        expenses: [...state.expenses.filter(keep), ...newExpenses].map((e) => expenses.find((u) => u.id === e.id) ?? e),
        adjustments: adjustment ? [...state.adjustments, adjustment] : state.adjustments,
      }
    }
    default:
      return state
  }
}

/** Exported for tests; the app goes through AppStateProvider. */
export function reducer(state: Plan, action: Action): Plan {
  switch (action.type) {
    case 'ACCOUNT':
      return {
        ...state,
        accounts: state.accounts.map((a) => (a.id === action.accountId ? accountReducer(a, action.action) : a)),
      }
    case 'SET_PLAN_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } }
    case 'ADD_ACCOUNT':
      return { ...state, accounts: [...state.accounts, action.payload] }
    case 'UPDATE_ACCOUNT': {
      const { id, ...changes } = action.payload
      return { ...state, accounts: state.accounts.map((a) => (a.id === id ? { ...a, ...changes } : a)) }
    }
    case 'DELETE_ACCOUNT':
      // A plan always has at least one account; the UI doesn't offer to delete
      // the last, and this makes sure nothing else can either.
      if (state.accounts.length <= 1) return state
      return {
        ...state,
        accounts: state.accounts.filter((a) => a.id !== action.payload),
        // Half a transfer is just money vanishing from the account that's left.
        transfers: state.transfers.filter(
          (t) => t.fromAccountId !== action.payload && t.toAccountId !== action.payload,
        ),
      }
    case 'ADD_TRANSFER':
      return { ...state, transfers: [...state.transfers, action.payload] }
    case 'UPDATE_TRANSFER':
      return { ...state, transfers: state.transfers.map((t) => (t.id === action.payload.id ? action.payload : t)) }
    case 'DELETE_TRANSFER':
      return { ...state, transfers: state.transfers.filter((t) => t.id !== action.payload) }
    case 'REPLACE_STATE':
      return action.payload
    default:
      return state
  }
}

const StateContext = createContext<Plan | null>(null)
const DispatchContext = createContext<React.Dispatch<Action> | null>(null)

interface ProviderProps {
  children: React.ReactNode
  /** The plan as it stood on the server when the app opened. */
  initial: Plan
  /**
   * Called with the new state after every change. Persistence lives outside
   * the reducer — see `useSyncedDocument`, which debounces these into saves —
   * so that the store stays a plain state machine with no idea a server exists.
   */
  onChange: (state: Plan) => void
}

export function AppStateProvider({ children, initial, onChange }: ProviderProps) {
  const [state, dispatch] = useReducer(reducer, initial)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const firstRender = useRef(true)
  useEffect(() => {
    // The state on mount *is* the server's copy. Reporting it as a change
    // would have every page load write it straight back.
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    onChangeRef.current(state)
  }, [state])

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{children}</DispatchContext.Provider>
    </StateContext.Provider>
  )
}

export function usePlan(): Plan {
  const ctx = useContext(StateContext)
  if (!ctx) throw new Error('usePlan must be used within AppStateProvider')
  return ctx
}

export function useAppDispatch(): React.Dispatch<Action> {
  const ctx = useContext(DispatchContext)
  if (!ctx) throw new Error('useAppDispatch must be used within AppStateProvider')
  return ctx
}
